// Locale resources for the static (non-backend) i18n path.
//
// Only English — the fallback language — is bundled into the entry chunk.
// The other locales are code-split into per-locale async chunks via
// loadLocaleResources() and registered with i18next at runtime (see
// src/app/i18n.ts). This keeps translation growth out of the main-bundle
// size budget: pipelines 23696/23698 failed when statically bundling all
// five locales pushed the entry chunk past its 800 kB brotli limit.
import enCommon from '@/shared/lib/i18n/locales/en/common.json'
import enAdmin from '@/shared/lib/i18n/locales/en/admin.json'
import enPages from '@/shared/lib/i18n/locales/en/pages.json'
import enComponents from '@/shared/lib/i18n/locales/en/components.json'
import enErrors from '@/shared/lib/i18n/locales/en/errors.json'
import enOnboarding from '@/shared/lib/i18n/locales/en/onboarding.json'
import enGlossary from '@/shared/lib/i18n/locales/en/glossary.json'

export const resources = {
  en: {
    common: enCommon,
    admin: enAdmin,
    pages: enPages,
    components: enComponents,
    errors: enErrors,
    onboarding: enOnboarding,
    glossary: enGlossary
  }
}

export type LocaleNamespaces = (typeof resources)['en']

// Lazily loaded locales. Each arrow function becomes one Vite chunk, fetched
// only when that language is actually needed.
const LAZY_LOCALE_LOADERS = {
  fi: async (): Promise<LocaleNamespaces> => ({
    common: (await import('@/shared/lib/i18n/locales/fi/common.json')).default,
    admin: (await import('@/shared/lib/i18n/locales/fi/admin.json')).default,
    pages: (await import('@/shared/lib/i18n/locales/fi/pages.json')).default,
    components: (await import('@/shared/lib/i18n/locales/fi/components.json')).default,
    errors: (await import('@/shared/lib/i18n/locales/fi/errors.json')).default,
    onboarding: (await import('@/shared/lib/i18n/locales/fi/onboarding.json')).default,
    glossary: (await import('@/shared/lib/i18n/locales/fi/glossary.json')).default
  }) as unknown as LocaleNamespaces,
  sv: async (): Promise<LocaleNamespaces> => ({
    common: (await import('@/shared/lib/i18n/locales/sv/common.json')).default,
    admin: (await import('@/shared/lib/i18n/locales/sv/admin.json')).default,
    pages: (await import('@/shared/lib/i18n/locales/sv/pages.json')).default,
    components: (await import('@/shared/lib/i18n/locales/sv/components.json')).default,
    errors: (await import('@/shared/lib/i18n/locales/sv/errors.json')).default,
    onboarding: (await import('@/shared/lib/i18n/locales/sv/onboarding.json')).default,
    glossary: (await import('@/shared/lib/i18n/locales/sv/glossary.json')).default
  }) as unknown as LocaleNamespaces,
  fr: async (): Promise<LocaleNamespaces> => ({
    common: (await import('@/shared/lib/i18n/locales/fr/common.json')).default,
    admin: (await import('@/shared/lib/i18n/locales/fr/admin.json')).default,
    pages: (await import('@/shared/lib/i18n/locales/fr/pages.json')).default,
    components: (await import('@/shared/lib/i18n/locales/fr/components.json')).default,
    errors: (await import('@/shared/lib/i18n/locales/fr/errors.json')).default,
    onboarding: (await import('@/shared/lib/i18n/locales/fr/onboarding.json')).default,
    glossary: (await import('@/shared/lib/i18n/locales/fr/glossary.json')).default
  }) as unknown as LocaleNamespaces,
  de: async (): Promise<LocaleNamespaces> => ({
    common: (await import('@/shared/lib/i18n/locales/de/common.json')).default,
    admin: (await import('@/shared/lib/i18n/locales/de/admin.json')).default,
    pages: (await import('@/shared/lib/i18n/locales/de/pages.json')).default,
    components: (await import('@/shared/lib/i18n/locales/de/components.json')).default,
    errors: (await import('@/shared/lib/i18n/locales/de/errors.json')).default,
    onboarding: (await import('@/shared/lib/i18n/locales/de/onboarding.json')).default,
    glossary: (await import('@/shared/lib/i18n/locales/de/glossary.json')).default
  }) as unknown as LocaleNamespaces
} as const

// Single source of truth for supported languages — bundled English plus the
// lazily loaded locales, so language pickers and validation guards see the
// full set while only English ships in the entry chunk.
export type SupportedLanguage = keyof typeof resources | keyof typeof LAZY_LOCALE_LOADERS

export const SUPPORTED_LANGUAGES = [
  ...Object.keys(resources),
  ...Object.keys(LAZY_LOCALE_LOADERS)
] as SupportedLanguage[]

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

/**
 * Load a lazily bundled locale's namespaces. Returns null for 'en' (already
 * bundled) and unknown codes. Results are memoized per locale.
 */
const loadedLocales = new Map<string, Promise<LocaleNamespaces>>()

export function loadLocaleResources(code: string): Promise<LocaleNamespaces> | null {
  if (!(code in LAZY_LOCALE_LOADERS)) return null
  const key = code as keyof typeof LAZY_LOCALE_LOADERS
  let pending = loadedLocales.get(key)
  if (!pending) {
    pending = LAZY_LOCALE_LOADERS[key]()
    loadedLocales.set(key, pending)
  }
  return pending
}

/**
 * Native display name (autonym) for a language code, e.g. 'fi' -> 'Suomi'.
 * Uses Intl.DisplayNames so new languages need no hardcoded name table.
 */
export function languageDisplayName(code: string): string {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code)
    if (name) return name.charAt(0).toLocaleUpperCase(code) + name.slice(1)
  } catch {
    // Unknown code — fall through to returning the code itself
  }
  return code
}
